export interface TelemetrySample {
  t: number;
  width: number;
  height: number;
  pose?: {
    landmarks?: Array<Array<[number, number, number, number?]>>;
  };
  hands?: {
    landmarks?: Array<Array<[number, number, number, number?]>>;
  };
}

export interface BufferInfo {
  frameCount: number;
  duration: number;
  firstFrame: number;
  lastFrame: number;
}

export interface TelemetryReader {
  getFrameAt(timeSec: number): TelemetrySample | null;
  ready: boolean;
  bufferInfo: BufferInfo;
  dispose(): void;
}

/**
 * Streaming NDJSON parser for telemetry data.
 * Buffers frames in timestamp order and provides time-based lookup.
 * Fires onReady when buffer >= minFrames or end-of-stream.
 */
export async function createTelemetryReader(
  url: string,
  onReady: (info: BufferInfo) => void,
  minFrames: number = 30
): Promise<TelemetryReader> {
  const buffer: TelemetrySample[] = [];
  let isReady = false;
  let abortController: AbortController | null = null;

  const getBufferInfo = (): BufferInfo => ({
    frameCount: buffer.length,
    duration: buffer.length > 0 ? buffer[buffer.length - 1].t - buffer[0].t : 0,
    firstFrame: buffer.length > 0 ? buffer[0].t : 0,
    lastFrame: buffer.length > 0 ? buffer[buffer.length - 1].t : 0,
  });

  const checkReady = () => {
    if (!isReady && buffer.length >= minFrames) {
      isReady = true;
      onReady(getBufferInfo());
    }
  };

  // Stream NDJSON data
  try {
    abortController = new AbortController();
    const response = await fetch(url, { 
      signal: abortController.signal,
      headers: { 'Accept': 'application/x-ndjson' }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch telemetry: ${response.status} ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No readable stream available');
    }

    const decoder = new TextDecoder();
    let partialLine = '';

    // Process stream chunks
    const processStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            // Process any remaining partial line
            if (partialLine.trim()) {
              try {
                const frame = JSON.parse(partialLine) as TelemetrySample;
                insertFrameInOrder(frame);
                checkReady();
              } catch (e) {
                console.warn('[ReadStream] Skipping malformed final line:', e);
              }
            }
            
            // End of stream - fire ready if not already
            if (!isReady) {
              isReady = true;
              onReady(getBufferInfo());
            }
            break;
          }

          // Decode and process lines
          const chunk = decoder.decode(value, { stream: true });
          const lines = (partialLine + chunk).split('\n');
          partialLine = lines.pop() || ''; // Keep incomplete line for next chunk

          for (const line of lines) {
            if (line.trim()) {
              try {
                const frame = JSON.parse(line) as TelemetrySample;
                insertFrameInOrder(frame);
                checkReady();
              } catch (e) {
                console.warn('[ReadStream] Skipping malformed line:', e);
              }
            }
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[ReadStream] Stream processing error:', error);
        }
      } finally {
        reader.releaseLock();
      }
    };

    // Start streaming (non-blocking)
    processStream();

  } catch (error) {
    console.error('[ReadStream] Failed to start stream:', error);
    throw error;
  }

  // Insert frame maintaining timestamp order
  const insertFrameInOrder = (frame: TelemetrySample) => {
    // Default empty arrays to prevent .length crashes
    if (frame.pose?.landmarks && !Array.isArray(frame.pose.landmarks)) {
      frame.pose.landmarks = [];
    }
    if (frame.hands?.landmarks && !Array.isArray(frame.hands.landmarks)) {
      frame.hands.landmarks = [];
    }

    // Binary search for insertion point
    let left = 0;
    let right = buffer.length;
    
    while (left < right) {
      const mid = Math.floor((left + right) / 2);
      if (buffer[mid].t <= frame.t) {
        left = mid + 1;
      } else {
        right = mid;
      }
    }
    
    buffer.splice(left, 0, frame);

    // Cap buffer size to prevent memory issues (~10 minutes at 30fps)
    const MAX_BUFFER_SIZE = 10000;
    if (buffer.length > MAX_BUFFER_SIZE) {
      const removeCount = buffer.length - MAX_BUFFER_SIZE;
      buffer.splice(0, removeCount); // Remove oldest frames (LRU eviction)
    }
  };

  // Time-based frame lookup
  const getFrameAt = (timeSec: number): TelemetrySample | null => {
    if (buffer.length === 0) return null;
    
    // Binary search for closest frame <= timeSec
    let left = 0;
    let right = buffer.length - 1;
    let result: TelemetrySample | null = null;
    
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const frame = buffer[mid];
      
      if (frame.t <= timeSec) {
        result = frame;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }
    
    return result;
  };

  const dispose = () => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    buffer.length = 0;
    isReady = false;
  };

  return {
    getFrameAt,
    get ready() { return isReady; },
    get bufferInfo() { return getBufferInfo(); },
    dispose
  };
}
